import { useEffect, useCallback } from 'react';

import { Controller, RegisterOptions, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import FormFieldset from '@/components/atoms/Fieldset/templates/FormFieldset';
import { Input } from '@/components/ui/input';
import { useIntegrations } from '@/hooks/useIntegrations';
import { Integration } from '@/shared/types/integration';
import { submitWithTrimming } from '@/util/util';
import { errorMessage } from '@/util/validation/helpers';

import { FieldsetProps } from '../../Fieldset';

interface Props extends FieldsetProps {
  integration: Integration;
  inputSize?: 'small' | 'medium' | 'large';
  name: string;
  validationRule?: RegisterOptions;
  readOnly?: boolean;
}

const SingleInputFieldset = ({
  integration,
  inputSize = 'large',
  name,
  validationRule = {},
  readOnly,
  ...fieldsetProps
}: Props) => {
  const { t } = useTranslation();
  const { updateIntegration, updateStatus } = useIntegrations(
    integration?.desk_id,
    integration?.integration_id
  );

  const nameConfigValue = integration?.config?.[name];
  const formMethods = useForm<{ [name: string]: string }>({
    mode: 'onChange',
    defaultValues: { [name]: nameConfigValue ?? '' },
  });
  const {
    control,
    reset,
    formState: { errors, isDirty },
  } = formMethods;

  useEffect(() => {
    if (nameConfigValue) {
      reset({ [name]: nameConfigValue ?? '' });
    }
  }, [nameConfigValue, name, reset]);

  const onSubmit = useCallback(
    (form) => {
      updateIntegration({ ...integration, config: { ...integration.config, ...form } });
    },
    [integration, updateIntegration]
  );

  const fieldErrorMessage =
    errorMessage({
      field: errors[name],
      minLength: validationRule?.minLength,
      maxLength: validationRule?.maxLength,
    }) ?? errors[name]?.message;

  return (
    <FormFieldset
      title={fieldsetProps.title}
      subtitle={fieldsetProps.subtitle}
      primaryButton={{
        children: t('common.save'),
        disabled: !isDirty,
        isLoading: updateStatus === 'pending',
      }}
      onSubmit={submitWithTrimming(formMethods, onSubmit)}
      externalLink={fieldsetProps.externalLink}
    >
      <Controller
        control={control}
        name={name}
        rules={validationRule}
        render={({ field }) => (
          <Input
            id={name}
            size={inputSize === 'medium' ? 'md' : 'sm'}
            aria-invalid={!!errors[name]}
            readOnly={readOnly}
            {...field}
            value={field.value ?? ''}
          />
        )}
      />
      {fieldErrorMessage && <p className="mt-1 text-sm text-warning">{fieldErrorMessage}</p>}
    </FormFieldset>
  );
};

export default SingleInputFieldset;
